import { create } from 'zustand';
import { emit } from '@/lib/events';
import type { Filters, PresetId } from './types';
import { F_DEFAULTS } from './filter-fns';
import { presetById } from './presets-config';

const DEBOUNCE_MS = 80;

interface FiltersState {
  raw: Filters;
  applied: Filters;
  presetActive: PresetId | null;
  set: (partial: Partial<Filters>) => void;
  reset: () => void;
  /** `silent` — применить без события аналитики (повтор пресета из URL). */
  applyPreset: (id: PresetId, opts?: { silent?: boolean }) => void;
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

export const useFiltersStore = create<FiltersState>((set, get) => ({
  raw: F_DEFAULTS,
  applied: F_DEFAULTS,
  presetActive: null,

  set: (partial) => {
    const newRaw = { ...get().raw, ...partial };
    set({ raw: newRaw, presetActive: null });
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      set({ applied: get().raw });
      debounceTimer = null;
    }, DEBOUNCE_MS);
  },

  reset: () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    set({ raw: F_DEFAULTS, applied: F_DEFAULTS, presetActive: null });
  },

  applyPreset: (id, opts) => {
    // Пресеты активной страны, а не только встроенные итальянские. Неизвестный
    // id (чужая страна в ссылке, опечатка) — no-op, а не сброс фильтров.
    const preset = presetById(id);
    if (!preset) return;
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    const newFilters: Filters = { ...F_DEFAULTS, ...preset.filters };
    set({ raw: newFilters, applied: newFilters, presetActive: id });
    if (!opts?.silent) emit('preset:selected', { preset_id: id });
  },
}));
